export type OnboardingPath = "file" | "integration" | "manual";

export type CreateResource = "location" | "practitioner" | "patient";

export type ImportRecordKind = "location" | "practitioner" | "patient";

export type ImportPreviewRecord = {
  id: string;
  kind: ImportRecordKind;
  primary: string;
  secondary: string;
};

export type MappingFieldKey =
  | "firstName"
  | "lastName"
  | "birthDate"
  | "role"
  | "email"
  | "careLevel"
  | "location"
  | "unit";

export type MappingField = {
  key: MappingFieldKey;
  labelKey: string;
  required: boolean;
  sample: string;
  fileColumn: string;
};

export type FieldMappings = Record<MappingFieldKey, string>;

export type DeviceEnrollmentShot = {
  image: string;
  titleKey: string;
  descriptionKey: string;
};

export type DeviceEnrollmentPhase = {
  labelKey: string;
  introKey: string;
  shots: DeviceEnrollmentShot[];
};

export const mappingFields: MappingField[] = [
  {
    key: "firstName",
    labelKey: "onboarding.mapping.fields.firstName",
    required: true,
    sample: "Lena",
    fileColumn: "vname",
  },
  {
    key: "lastName",
    labelKey: "onboarding.mapping.fields.lastName",
    required: true,
    sample: "Fischer",
    fileColumn: "nname",
  },
  {
    key: "birthDate",
    labelKey: "onboarding.mapping.fields.birthDate",
    required: false,
    sample: "14.03.1938",
    fileColumn: "geb_dat",
  },
  {
    key: "role",
    labelKey: "onboarding.mapping.fields.role",
    required: false,
    sample: "Betreuungskraft",
    fileColumn: "funktion",
  },
  {
    key: "email",
    labelKey: "onboarding.mapping.fields.email",
    required: false,
    sample: "l.fischer@einrichtung.de",
    fileColumn: "mail",
  },
  {
    key: "careLevel",
    labelKey: "onboarding.mapping.fields.careLevel",
    required: false,
    sample: "PG 3",
    fileColumn: "pg",
  },
  {
    key: "location",
    labelKey: "onboarding.mapping.fields.location",
    required: true,
    sample: "München Zentrum",
    fileColumn: "einrichtung_name",
  },
  {
    key: "unit",
    labelKey: "onboarding.mapping.fields.unit",
    required: true,
    sample: "Schwabing Nord",
    fileColumn: "wohnbereich",
  },
];

export const createDefaultMappings = (): FieldMappings =>
  mappingFields.reduce<FieldMappings>((mappings, field) => {
    mappings[field.key] = field.fileColumn;
    return mappings;
  }, {} as FieldMappings);

export const importPreviewRecords: ImportPreviewRecord[] = [
  { id: "location-1", kind: "location", primary: "München Zentrum", secondary: "Standort" },
  {
    id: "location-2",
    kind: "location",
    primary: "Schwabing Nord",
    secondary: "Wohnbereich · München Zentrum",
  },
  {
    id: "location-3",
    kind: "location",
    primary: "Klenzestr. 61",
    secondary: "Wohnbereich · München Zentrum",
  },
  {
    id: "practitioner-1",
    kind: "practitioner",
    primary: "Lena Fischer",
    secondary: "Betreuungskraft · l.fischer@einrichtung.de",
  },
  { id: "practitioner-2", kind: "practitioner", primary: "Tobias Krause", secondary: "Betreuungskraft" },
  {
    id: "practitioner-3",
    kind: "practitioner",
    primary: "Miriam Halder",
    secondary: "PDL · m.halder@einrichtung.de",
  },
  {
    id: "practitioner-4",
    kind: "practitioner",
    primary: "Jonas Reiter",
    secondary: "Betreuungskraft · j.reiter@einrichtung.de",
  },
  { id: "practitioner-5", kind: "practitioner", primary: "Sara Neumann", secondary: "Betreuungskraft" },
  { id: "patient-1", kind: "patient", primary: "Gertrud Weiß", secondary: "14.03.1938 · PG 3" },
  { id: "patient-2", kind: "patient", primary: "Heinrich Osterloh", secondary: "02.11.1941 · PG 4" },
  { id: "patient-3", kind: "patient", primary: "Anneliese Kern", secondary: "27.06.1936 · PG 2" },
  { id: "patient-4", kind: "patient", primary: "Wilhelm Bruns", secondary: "09.01.1943 · PG 3" },
  { id: "patient-5", kind: "patient", primary: "Margarete Thiel", secondary: "18.08.1939 · PG 5" },
  { id: "patient-6", kind: "patient", primary: "Erika Lindner", secondary: "03.05.1945 · PG 2" },
];

export const deviceEnrollmentPhases: DeviceEnrollmentPhase[] = [
  {
    labelKey: "onboarding.device.phases.activation.label",
    introKey: "onboarding.device.phases.activation.intro",
    shots: [
      {
        image: "/images/onboarding/device/01-open-settings.jpg",
        titleKey: "onboarding.device.shots.openSettings.title",
        descriptionKey: "onboarding.device.shots.openSettings.description",
      },
      {
        image: "/images/onboarding/device/02-device-settings.jpg",
        titleKey: "onboarding.device.shots.deviceSettings.title",
        descriptionKey: "onboarding.device.shots.deviceSettings.description",
      },
      {
        image: "/images/onboarding/device/03-caregiver-login.jpg",
        titleKey: "onboarding.device.shots.caregiverLogin.title",
        descriptionKey: "onboarding.device.shots.caregiverLogin.description",
      },
      {
        image: "/images/onboarding/device/04-activate.jpg",
        titleKey: "onboarding.device.shots.activate.title",
        descriptionKey: "onboarding.device.shots.activate.description",
      },
      {
        image: "/images/onboarding/device/05-service-code.jpg",
        titleKey: "onboarding.device.shots.serviceCode.title",
        descriptionKey: "onboarding.device.shots.serviceCode.description",
      },
      {
        image: "/images/onboarding/device/06-confirm.jpg",
        titleKey: "onboarding.device.shots.confirm.title",
        descriptionKey: "onboarding.device.shots.confirm.description",
      },
    ],
  },
  {
    labelKey: "onboarding.device.phases.enrollment.label",
    introKey: "onboarding.device.phases.enrollment.intro",
    shots: [
      {
        image: "/images/onboarding/device/07-credentials.jpg",
        titleKey: "onboarding.device.shots.credentials.title",
        descriptionKey: "onboarding.device.shots.credentials.description",
      },
      {
        image: "/images/onboarding/device/08-select-site.jpg",
        titleKey: "onboarding.device.shots.selectSite.title",
        descriptionKey: "onboarding.device.shots.selectSite.description",
      },
      {
        image: "/images/onboarding/device/09-select-unit.jpg",
        titleKey: "onboarding.device.shots.selectUnit.title",
        descriptionKey: "onboarding.device.shots.selectUnit.description",
      },
      {
        image: "/images/onboarding/device/10-device-name.jpg",
        titleKey: "onboarding.device.shots.deviceName.title",
        descriptionKey: "onboarding.device.shots.deviceName.description",
      },
      {
        image: "/images/onboarding/device/11-registration-confirmed.jpg",
        titleKey: "onboarding.device.shots.registrationConfirmed.title",
        descriptionKey: "onboarding.device.shots.registrationConfirmed.description",
      },
    ],
  },
  {
    labelKey: "onboarding.device.phases.start.label",
    introKey: "onboarding.device.phases.start.intro",
    shots: [
      {
        image: "/images/onboarding/device/12-caregiver-selection.jpg",
        titleKey: "onboarding.device.shots.caregiverSelection.title",
        descriptionKey: "onboarding.device.shots.caregiverSelection.description",
      },
    ],
  },
];
